 
import {
    Dispatch, SetStateAction, useRef, useState,
} from 'react';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import { NSTable, NSButton, NSDropdown } from 'bricks';
import { PRO_FORMA_UNIT_TO_FIELD_VALUES, UNIT_TYPE_NAMES } from 'constants/unitTypes';
import { DEVELOPMENT_SOURCE_METRIC, PRO_FORMA_METRIC_TO_FIELD_VALUES, PRO_FORMA_SOURCE_METRIC_OPTIONS } from 'constants/sources';
import {
    DevelopmentUnitOfMeasureTypes,
    IProFormaSourceTableRow,
    ISourcesSummary,
    IUnitTypeOption,
    IProFormaPageLocalStorage,
    IProFormaLocalStorage,
    ICreateProFormaSourceRequestData,
    SourceCalculatedOn,
} from 'views/ProFormaTable/types';
import { isDebtSource } from 'utils/sourceTypeUtils';
import { Periods } from 'components/ProFormaReturnsTable/constant';
import RenderIf from 'components/RenderIf/RenderIf';
import { numeralFormatterCurrency } from 'ns_libs/formatter';
import { PageSpecificKeys, useLocalStorage } from 'helpers/localStorage';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import { formatCashFlowPeriods, getSaleIndexForCadence } from 'views/ProFormaTable/helpers';
import { useCustomScroll } from 'views/ProFormaTable/hooks/useCustomScroll';
import AddSourceModal from './AddSourceModal/AddSourceModal';
import Source from './Source/Source';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import PeriodCellValue from '../DevelopmentUsesTable/PeriodCellValue/PeriodCellValue';

export interface IDevelopmentSourcesTable {
    sources: ISourcesSummary;
    unitType: IUnitTypeOption;
    setUnitType: Dispatch<SetStateAction<IUnitTypeOption | null>>;
    unitTypeOptions: IUnitTypeOption[];
    hasWriteAccessToProForma: boolean;
}

const DevelopmentSourcesTable = ({
    sources,
    unitType,
    setUnitType,
    unitTypeOptions,
    hasWriteAccessToProForma,
}: IDevelopmentSourcesTable) => {
    const {
        proForma, handleCreateProFormaSource, handleUpdateSourcesMetricType, isUpdatingSourcesMetricType,
        showCents, cashFlowPeriodsByCadence, periodCadence, sourceTypes, operatingInfo,
    } = useProFormaSocketContext();
    const {
        tableRef,
        handleTableScroll,
    } = useCustomScroll(
        'sources-table',
        ['uses-table'],
    );
    const [proFormaPageLocalStorage, setProFormaPageLocalStorage] = useLocalStorage<IProFormaPageLocalStorage>(PageSpecificKeys.ProFormaPage, {});
    const currentProFormaLocalStorage = proForma.id in proFormaPageLocalStorage ? proFormaPageLocalStorage[proForma.id] : {} as IProFormaLocalStorage;
    const [openAddSourceModal, setOpenAddSourceModal] = useState(false);
    const [isCreateProFormaSourcePending, setIsCreateProFormaSourcePending] = useState(false);
    const [newDebtMetric, setNewDebtMetric] = useState<DEVELOPMENT_SOURCE_METRIC | null>(null);
    const [isDebtMetricModalOpen, setIsDebtMetricModalOpen] = useState(false);
    const theadRef = useRef<HTMLTableSectionElement>(null);

    const selectedMetricType = sources?.sources?.find(source => source.metricType !== null)?.metricType || DEVELOPMENT_SOURCE_METRIC.DEBT_YIELD;

    const toggleAddSourceModal = () => setOpenAddSourceModal(!openAddSourceModal);
    const toggleDebtMetricModal = () => setIsDebtMetricModalOpen(!isDebtMetricModalOpen);

    const createSourceCallback = (isSuccess: boolean = true) => {
        setIsCreateProFormaSourcePending(false);
        if (isSuccess) setOpenAddSourceModal(false);
    };

    const handleClickAddSource = (sourceTypeId: string, sourceName: string) => {
        setIsCreateProFormaSourcePending(true);

        const max = !sources?.sources?.length ? 0 : Math.max(...sources?.sources?.map(source => source.traunch || 0));

        let sourceData: ICreateProFormaSourceRequestData = {
            sourceTypeId,
            name: sourceName,
            traunch: max + 1,
            amount: '0',
        };

        if (isDebtSource(sourceTypeId, sourceTypes)) {
            sourceData = {
                ...sourceData,
                calculatedOn: 'amount' as SourceCalculatedOn,
            };
        }

        handleCreateProFormaSource(sourceData, createSourceCallback);
    };

    const handleUpdateUnitType = (option: IUnitTypeOption) => {
        setUnitType(option);
        const updatedProFormaStorage = {
            ...proFormaPageLocalStorage,
            [proForma.id]: { ...currentProFormaLocalStorage, developmentUnitType: option.value },
        };
        setProFormaPageLocalStorage(updatedProFormaStorage as IProFormaPageLocalStorage);
    };

    const handleUpdateDebtMetric = () => {
        if (newDebtMetric) {
            handleUpdateSourcesMetricType(proForma.id, newDebtMetric, toggleDebtMetricModal);
        }
    };

    const selectedUnitTotal = sources.totals[PRO_FORMA_UNIT_TO_FIELD_VALUES[unitType.value as DevelopmentUnitOfMeasureTypes]];

    const unavailableUnitTypes: string[] = [];

    unitTypeOptions.map(option => {
        const costPerUnitTotal = sources.totals[PRO_FORMA_UNIT_TO_FIELD_VALUES[option.value as DevelopmentUnitOfMeasureTypes]];
        return costPerUnitTotal === null ? unavailableUnitTypes.push(option.value) : null;
    });

    const updatedUnitTypeOptions = unitTypeOptions.map(option => ({
        ...option,
        disabled: unavailableUnitTypes.includes(option.value),
    }));

    const saleIndex = getSaleIndexForCadence(operatingInfo.saleMonth, periodCadence);
    const projectionPeriods = formatCashFlowPeriods(cashFlowPeriodsByCadence, periodCadence, !sources?.sources?.length, saleIndex);

    return (
        <div>
            <div
                className="d-flex m-3 scrollable-container pb-1"
                style={{ maxHeight: 'none' }}
                ref={tableRef}
                onScroll={() => handleTableScroll('table')}
            >
                <NSTable
                    className="NSTable--sticky-headers NSTable--sticky-headers--full-width NSTable--sticky-headers--six-sticky-columns"
                    style={{ maxHeight: 'none' }}
                >
                    <thead className="NSTable__thead" ref={theadRef}>
                        <tr className="NSTable__thead__tr">
                            <th className="NSTable__thead__tr__th NSTable__thead__tr__th--right-border align-middle py-1">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center ml-1">
                                        <span className="ml-0">Name</span>
                                    </div>
                                    <NSButton
                                        className="px-2 text-muted cursor--pointer hover:text-dark"
                                        size="sm"
                                        iconOnly
                                        icon={faPlus}
                                        callback={toggleAddSourceModal}
                                    />
                                </div>
                            </th>
                            <th className="NSTable__thead__tr__th align-middle py-1">
                                <div className="d-flex justify-content-end">
                                    <NSDropdown
                                        options={PRO_FORMA_SOURCE_METRIC_OPTIONS}
                                        selectedOption={PRO_FORMA_SOURCE_METRIC_OPTIONS.find(option => option.value === selectedMetricType)}
                                        handleSelectOption={option => {
                                            setNewDebtMetric(option.value);
                                            toggleDebtMetricModal();
                                        }}
                                        badgeColor="dark-lighten"
                                        isBadgeToggle
                                        menuRight
                                        useBodyContainer
                                    />
                                </div>
                            </th>
                            <th className="NSTable__thead__tr__th align-middle py-1">
                                <div className="d-flex justify-content-end">Fund. traunch</div>
                            </th>
                            <th className="NSTable__thead__tr__th align-middle py-1 px-1 text-nowrap">
                                <div className="d-flex justify-content-end">% of sources</div>
                            </th>
                            <th className="NSTable__thead__tr__th align-middle py-1 text-nowrap">
                                <div className="text-right">
                                    <span className="mr-1">Cost per</span>
                                    <NSDropdown
                                        options={updatedUnitTypeOptions}
                                        selectedOption={{ label: UNIT_TYPE_NAMES[unitType.value].abbreviated, value: unitType.value }}
                                        handleSelectOption={option => handleUpdateUnitType(option)}
                                        containerClassName="ReadOnlyWrapper--enable-pointer-events"
                                        badgeColor="dark-lighten"
                                        isBadgeToggle
                                        useBodyContainer
                                        menuRight
                                    />
                                </div>
                            </th>
                            <th className="NSTable__thead__tr__th align-middle text-right text-nowrap py-1">
                                Amount
                            </th>
                            {projectionPeriods.map(periodInfo => (
                                <th
                                    key={periodInfo.value}
                                    className="NSTable__thead__tr__th align-middle
                                NSTable__thead__tr__th--grey-bg NSTable__thead__tr__th--width-150 py-1"
                                >
                                    <div
                                        className={classNames([
                                            ['h6 NSTable__thead__tr__th--grey-text text-uppercase text-nowrap text-right mb-0'], {
                                                'NSTable__thead__tr__th--opacity-3': periodInfo.isPlaceholder,
                                            },
                                        ])}
                                    >
                                        {periodInfo.period}
                                    </div>
                                    <div
                                        className={classNames([
                                            ['h6 text-uppercase text-right text-nowrap mt-1'], {
                                                'NSTable__thead__tr__th--opacity-3': periodInfo.isPlaceholder,
                                            },
                                        ])}
                                    >
                                        {periodInfo.label}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody className="NSTable__tbody">
                        <RenderIf isTrue={sources?.sources?.length}>
                            {sources?.sources?.map(source => (
                                <Source
                                    key={source.id}
                                    source={source}
                                    totalSourcesAmount={sources?.sources?.length}
                                    unitType={unitType.value as DevelopmentUnitOfMeasureTypes}
                                    selectedMetric={PRO_FORMA_METRIC_TO_FIELD_VALUES[selectedMetricType] as keyof IProFormaSourceTableRow}
                                    hasWriteAccessToProForma={hasWriteAccessToProForma}
                                    projectionPeriods={projectionPeriods}
                                />
                            ))}
                        </RenderIf>

                        <RenderIf isTrue={!sources?.sources?.length}>
                            <tr className="NSTable__tbody_tr NSTable__tbody__tr--level-1">
                                <td className="NSTable__tbody__tr__td align-middle" colSpan={6}>
                                    <div className="text-center text-dark mt-5">Your added sources will appear here.</div>
                                    <div className="text-center mb-5">
                                        Click the plus
                                        {' '}
                                        <span className="text-dark">[+]</span>
                                        {' '}
                                        symbol in the
                                        {' '}
                                        <span className="text-dark">Name</span>
                                        {' '}
                                        header to
                                        add your first source.
                                    </div>
                                </td>
                                {projectionPeriods.map(index => (
                                    <PeriodCellValue
                                        key="sources-header-period"
                                        tooltipId="sources-header-period"
                                        period={{ label: 'MON YEAR', value: '', isPlaceholder: true }}
                                        value="—"
                                        cellClassName="NSTable__tbody__tr__td text-dark text-right"
                                    />
                                ))}
                            </tr>
                        </RenderIf>
                    </tbody>

                    <tfoot className="NSTable__tfoot font-weight-bold">
                        <tr className="NSTable__tfoot__tr">
                            <td className="NSTable__tfoot__tr__td NSTable__tfoot__tr__td--right-border text-left">
                                <div className="ml-2">Total</div>
                            </td>

                            <td className="NSTable__tfoot__tr__td" />
                            <td className="NSTable__tfoot__tr__td" />
                            <td className="NSTable__tfoot__tr__td text-right">{sources?.sources?.length ? '100.00%' : '—'}</td>
                            <td className="NSTable__tfoot__tr__td text-right text-nowrap">
                                {selectedUnitTotal !== null ? numeralFormatterCurrency(selectedUnitTotal) : '—'}
                            </td>
                            <td className="NSTable__tfoot__tr__td text-right text-nowrap">
                                {sources.totals?.amount !== null ? numeralFormatterCurrency(sources.totals?.amount, showCents ? '$0,0.00' : '$0,0') : '—'}
                            </td>
                            {projectionPeriods.map((period, index) => {
                                let periodsTotals = sources.periodsTotals || [];
                                if (periodCadence === Periods.ANNUALLY) periodsTotals = sources.yearlyPeriodsTotals || [];
                                const targetPeriod = periodsTotals[index];
                                return (
                                    <PeriodCellValue
                                        key={`sources-totals-period-${period.value}`}
                                        tooltipId={`sources-totals-period-${index}`}
                                        period={period}
                                        value={targetPeriod?.amountToFund !== null
                                            ? numeralFormatterCurrency(targetPeriod?.amountToFund, showCents ? '$0,0.00' : '$0,0') : '—'}
                                        cellClassName="NSTable__tfoot__tr__td text-dark text-right"
                                    />
                                );
                            })}
                        </tr>
                    </tfoot>
                </NSTable>
            </div>

            <RenderIf isTrue={openAddSourceModal}>
                <AddSourceModal
                    toggle={toggleAddSourceModal}
                    isOpen={openAddSourceModal}
                    isLoading={isCreateProFormaSourcePending}
                    handleClickAddSource={handleClickAddSource}
                />
            </RenderIf>

            <RenderIf isTrue={isDebtMetricModalOpen}>
                <ConfirmationModal
                    toggle={toggleDebtMetricModal}
                    isOpen={isDebtMetricModalOpen}
                    onConfirm={handleUpdateDebtMetric}
                    onCancel={() => { setNewDebtMetric(null); toggleDebtMetricModal(); }}
                    modalHeaderText="Update debt sizing metric"
                    modalFooterButtonText="Yes, update"
                    modalFooterButtonIsLoading={isUpdatingSourcesMetricType}
                    danger
                >
                    <div className="text-dark font-weight-bold mb-1">Are you sure you want to change the debt sizing metric?</div>
                    <div>This will remove any previously entered data.</div>
                </ConfirmationModal>
            </RenderIf>
        </div>
    );
};

export default DevelopmentSourcesTable;
